import { asDateString, stripInternalKeys } from './member-update-payload.util';
import { AuthService } from './auth.service';

export interface VisitorPayloadOptions {
  includeCompanyBranch?: boolean;
  includeFeeDetails?: boolean;
}

/** Body for PATCH /api/v1/branches/:branchId/visitors/:id (UpdateVisitorDto). */
export function buildVisitorUpdatePayload(
  visitor: object,
  options: VisitorPayloadOptions = {},
): Record<string, unknown> {
  const v = (visitor || {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {};

  if (v.name) payload.name = v.name;

  const dob = asDateString(v.dateOfBirth);
  if (dob) payload.dateOfBirth = dob;

  if (v.email !== undefined && v.email !== null) payload.email = v.email;
  if (v.gender) payload.gender = v.gender;

  const phone = v.phone ?? v.contact;
  if (phone) payload.phone = phone;

  const visitDate = asDateString(v.date);
  if (visitDate) payload.date = visitDate;

  if (v.dayPass) payload.dayPass = v.dayPass;
  if (v.visitingFee !== undefined && v.visitingFee !== null) {
    payload.visitingFee = String(v.visitingFee);
  }

  if (options.includeCompanyBranch) {
    if (v.company) payload.company = v.company;
    if (v.branch) payload.branch = v.branch;
  }

  if (options.includeFeeDetails && v.feeDetails) {
    payload.feeDetails = stripInternalKeys(v.feeDetails);
  }

  return payload;
}

/** Body for POST /api/v1/branches/:branchId/visitors (CreateVisitorDto). */
export function buildVisitorCreatePayload(visitor: object): Record<string, unknown> {
  const payload = buildVisitorUpdatePayload(visitor, {
    includeCompanyBranch: true,
    includeFeeDetails: true,
  });
  const v = (visitor || {}) as Record<string, unknown>;
  if (v.createdBy) payload.createdBy = v.createdBy;
  return payload;
}

export interface VisitorPayFundOptions {
  scope?: string;
  /** Branch id from the request URL path (falls back to logged-in user's branch). */
  branchId?: string;
  initiatedFrom?: string;
  name?: string;
  tagNumber?: string;
}

/** Body for POST .../visitors/:id/actions/pay-fund (PayFundDto). */
export function buildVisitorPayFundPayload(
  fund: object,
  packageObj?: object,
  options: VisitorPayFundOptions = {},
): Record<string, unknown> {
  const f = (fund || {}) as Record<string, unknown>;
  const fundPayload: Record<string, unknown> = {};

  const setFundStr = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== '') {
      fundPayload[key] = String(value);
    }
  };

  if (f.dayPass) fundPayload.dayPass = f.dayPass;
  if (f.invoice) fundPayload.invoice = f.invoice;
  if (f.pay !== undefined && f.pay !== null) fundPayload.pay = String(f.pay);
  if (f.discount !== undefined && f.discount !== null) {
    fundPayload.discount = String(f.discount);
  }
  if (f.tax !== undefined && f.tax !== null) fundPayload.tax = String(f.tax);
  if (f.taxRate !== undefined && f.taxRate !== null) {
    fundPayload.taxRate = Number(f.taxRate);
  }
  if (f.fee !== undefined && f.fee !== null) fundPayload.fee = String(f.fee);
  if (f.type) fundPayload.type = String(f.type);
  if (f.status) fundPayload.status = f.status;

  const feeDate = asDateString(f.date);
  if (feeDate) fundPayload.date = feeDate;

  const createdDate = asDateString(f.createdDate);
  if (createdDate) fundPayload.createdDate = createdDate;

  if (f.createdBy) fundPayload.createdBy = f.createdBy;
  if (f.paymentMode) fundPayload.paymentMode = f.paymentMode;

  setFundStr('trainerName', f.trainerName);
  setFundStr('taxCalculationType', f.taxCalculationType);
  setFundStr('numberOfVisitors', f.numberOfVisitors);
  setFundStr('initiatedFrom', f.initiatedFrom ?? options.initiatedFrom);
  setFundStr('scope', f.scope ?? options.scope);
  setFundStr('day', f.day);

  if (f.isDelete !== undefined && f.isDelete !== null) {
    fundPayload.isDelete = Boolean(f.isDelete);
  }

  if (Array.isArray(f.months) && f.months.length > 0) {
    fundPayload.months = f.months;
  }
  if (Array.isArray(f.years) && f.years.length > 0) {
    fundPayload.years = f.years;
  }

  const payload: Record<string, unknown> = { fund: fundPayload };

  if (packageObj) {
    const pkg = packageObj as Record<string, unknown>;
    const packagePayload: Record<string, unknown> = {};
    if (pkg._id) packagePayload._id = pkg._id;
    if (pkg.name) packagePayload.name = pkg.name;
    if (pkg.fees !== undefined && pkg.fees !== null) {
      packagePayload.fees = String(pkg.fees);
    }
    payload.packageObj = packagePayload;
  }

  const setRootStr = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== '') {
      payload[key] = String(value);
    }
  };

  const loggedInUser = AuthService.getLoggedInUser();
  setRootStr('company', AuthService.getLoggedInCompany());
  setRootStr('branch', options.branchId ?? AuthService.getLoggedInBranch());
  setRootStr('scope', options.scope ?? f.scope);
  setRootStr('initiatedFrom', options.initiatedFrom ?? f.initiatedFrom);
  setRootStr('name', options.name);
  setRootStr('activityBy', loggedInUser?.name);
  setRootStr('tagNumber', options.tagNumber);

  return payload;
}

function buildVisitorCurrentUserData(
  currentUser: Record<string, unknown>,
): Record<string, unknown> {
  const currentUserData: Record<string, unknown> = {};

  if (currentUser.name != null && currentUser.name !== '') {
    currentUserData.name = String(currentUser.name);
  }
  if (currentUser.branch != null && currentUser.branch !== '') {
    currentUserData.branch = String(currentUser.branch);
  }
  if (currentUser.company != null && currentUser.company !== '') {
    currentUserData.company = String(currentUser.company);
  }

  return currentUserData;
}

/** Body for POST .../visitors/actions/delete-fee (DeleteFeeDto). */
export function buildVisitorDeleteFeePayload(
  visitorId: string,
  feeDetail: Record<string, unknown>,
  currentUser: Record<string, unknown>,
): Record<string, unknown> {
  return {
    _id: String(visitorId),
    feeDetail: {
      _id: String(feeDetail._id),
    },
    currentUserData: buildVisitorCurrentUserData(currentUser),
  };
}

/** URL for POST .../branches/:branchId/visitors/actions/update-invoice-date */
export function visitorUpdateInvoiceDateUrl(branchId: string): string {
  return `branches/${branchId}/visitors/actions/update-invoice-date`;
}

/** Body for POST .../visitors/actions/update-invoice-date */
export function buildVisitorUpdateInvoiceDatePayload(
  visitorId: string,
  feeId: string,
  newInvoiceDate: string,
  currentUser: Record<string, unknown>,
): Record<string, unknown> {
  return {
    _id: String(visitorId),
    id: String(feeId),
    visitorId: String(visitorId),
    feeDetail: {
      _id: String(feeId),
    },
    newInvoiceDate,
    currentUserData: buildVisitorCurrentUserData(currentUser),
  };
}

/** URL for POST .../branches/:branchId/visitors/actions/update-payment-mode */
export function visitorUpdatePaymentModeUrl(branchId: string): string {
  return `branches/${branchId}/visitors/actions/update-payment-mode`;
}

/** Body for POST .../visitors/actions/update-payment-mode */
export function buildVisitorUpdatePaymentModePayload(
  visitorId: string,
  feeId: string,
  paymentMode: string,
  currentUser: Record<string, unknown>,
): Record<string, unknown> {
  return {
    _id: String(visitorId),
    id: String(feeId),
    visitorId: String(visitorId),
    feeDetail: {
      _id: String(feeId),
    },
    paymentMode,
    currentUserData: buildVisitorCurrentUserData(currentUser),
  };
}
